import { useEffect, useRef, useState } from "react";
import { paper, Point, Size, Rectangle, Path, Segment, PointText, Group } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import Debug from "./Debug";
import { Button, Modal, Form } from "react-bootstrap";
import { colors } from "../../../helpers/colors";
import { useToasts } from "react-toast-notifications";
import DownloadMenu from "../../shared/DownloadMenu";
import { MULTIPLIER, PADDING, POINTS_OFFSET, SCALE_INTERVAL } from "../utils/constants";
import { toStartOffset, scaleLabelChainage, chainageToCanvasX } from "../../shared/chainage";
import { toRadians, toDegree } from "../utils/mathUtils";
import { calculateAlpha, calculateQuadrants, calculateDopp, calculateApDir } from "../utils/calculations";
import { solveCircularAlphaZero } from "../utils/alphaZero";
import { computeAsf } from "../../shared/asf";
import { correctApparentDip } from "../../shared/inclination";
import { createTraceAlerts, changedTraces, CLIP_ALERT, drawnPastLog, runsPastLog } from "../../shared/traceAlerts";
import { labelCell } from "../../shared/labelField";
import { faceLogPointRow } from "../../shared/faceLogRows";
import { faceLogSecondPoint } from "../../shared/orientationSources";
import {
  TRUESPACE_LABEL,
  openTrueSpace,
  projectPointFromEvent,
  hitsLink,
} from "../../shared/trueSpace";
import { drawTunnelHeader } from "../../shared/tunnelHeader";
import { chainageExtent, fullLogExtent, NO_EXTENT } from "../../shared/traceExtent";

/**
 * Output Component for Circular Tunnel
 * Handles trace visualization and all mathematical calculations
 */
const Output = (props) => {
  const { addToast, removeAllToasts } = useToasts();
  const [uservariables, setUserVariables] = useState([]);
  const [show, setShow] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState(false);
  const handleClose = () => setShow(false);
  
  let legendpath = {};
  const tracedimensions = props.dimensions[0];
  const multiplier = MULTIPLIER;
  const padding = PADDING;
  let points = POINTS_OFFSET;
  const length = (tracedimensions.endrd - tracedimensions.startrd) * multiplier;
  // Chainage origin: x=padding represents the Start Chainage, not 0. Traces are
  // anchored relative to this offset and the scale prints absolute chainages.
  const startOffsetRD = toStartOffset(tracedimensions.startrd);
  const r = tracedimensions.radiusofcrown * multiplier;
  const hw = r * 2;
  const Theta = 0;
  const Arc = (Math.PI * r) / 2;
  const ArcFull = Arc * 2;
  let Ymax = padding + 2 * (Arc + Arc);

  const updateUserVariable = (usrvar) => {
    setUserVariables(usrvar);
  };

  // Box of the TrueSpace link drawn in the output notes, in project
  // coordinates. Set while drawing; read by the canvas mouse handlers below.
  const trueSpaceLinkBounds = useRef(null);

  const canvasProjectPoint = (e) => {
    const view = paper.view;
    if (!view) return null;
    return projectPointFromEvent(
      e,
      e.currentTarget.getBoundingClientRect(),
      view.viewSize.width
    );
  };

  const handleCanvasClick = (e) => {
    if (hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current)) {
      openTrueSpace();
    }
  };

  const handleCanvasMouseMove = (e) => {
    const over = hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current);
    e.currentTarget.style.cursor = over ? "pointer" : "default";
  };

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dimensions, props.showdebug]);

  /**
   * Create scale markings on the log
   */
  const createScale = () => {
    if (tracedimensions.iscrown) {
      Ymax = padding + 2 * Arc + Arc;
    }
    const scalearr = [];
    for (let i = padding; i <= length + padding; i += multiplier * SCALE_INTERVAL) {
      const text = new PointText(new Point(i, Ymax + 15));
      text.justification = "center";
      text.fillColor = "black";
      text.content = scaleLabelChainage(i, padding, multiplier, startOffsetRD);
      scalearr.push([i, Ymax + 10]);
      scalearr.push([i, Ymax]);
      if (i + multiplier * SCALE_INTERVAL <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([i + multiplier * j, Ymax]);
          scalearr.push([i + multiplier * j, Ymax + 5]);
          scalearr.push([i + multiplier * j, Ymax]);
        }
        scalearr.push([i + multiplier * SCALE_INTERVAL, Ymax]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
    if (tracedimensions.iscrown) {
      Ymax = padding + 2 * (Arc + Arc);
    }
  };

  /**
   * Create tunnel log structure
   */
  const createLog = () => {
    const style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };
    let path, path1;
    if (!tracedimensions.iscrown) {
      path = new Path.Rectangle(new Rectangle(new Point(padding, padding), new Size(length, 2 * (Arc + Arc))));
      path.style = style;
      path1 = new Path.Rectangle(new Rectangle(new Point(padding, padding + Arc), new Size(length, 2 * Arc)));
      path1.style = style;
    } else {
      path = new Path.Rectangle(new Rectangle(new Point(padding, padding + Arc), new Size(length, 2 * Arc)));
      path.style = style;
    }

    const path2 = new Path.Circle(path.bounds.rightCenter.add([r, 0]), r);
    path2.style = style;
    legendpath["path2"] = path2;

    // SPL line - dotted vertical line passing through center of circular tunnel
    const splLine = new Path.Line(path2.bounds.topCenter, path2.bounds.bottomCenter);
    splLine.strokeColor = "black";
    splLine.dashArray = [4, 4];

    const path3 = new Path.Line(path.bounds.leftCenter, path2.bounds.rightCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];


    if (!tracedimensions.iscrown) {
      const path4 = new Path.Arc(path.bounds.topRight, path.bounds.rightCenter.add([2 * r, -r / 2]), path.bounds.rightCenter.add([2 * r, 0]));
      path4.strokeColor = "black";
      path4.dashArray = [14, 5];
      const path5 = new Path.Arc(path.bounds.bottomRight, path.bounds.rightCenter.add([2 * r, r / 2]), path.bounds.rightCenter.add([2 * r, 0]));
      path5.strokeColor = "black";
      path5.dashArray = [14, 5];
      const path6 = new Path.Arc(path1.bounds.topRight, path.bounds.rightCenter.add([r - 3, -r - 3]), path.bounds.rightCenter.add([r, -r]));
      path6.strokeColor = "black";
      path6.dashArray = [5, 5];
      const path7 = new Path.Arc(path1.bounds.bottomRight, path.bounds.rightCenter.add([r - 3, r + 3]), path.bounds.rightCenter.add([r, r]));
      path7.strokeColor = "black";
      path7.dashArray = [5, 5];
    } else {
      const path4 = new Path(new Segment(path.bounds.topRight), new Segment(path2.bounds.topLeft));
      path4.strokeColor = "black";
      path4.dashArray = [3, 5];
      const path5 = new Path(new Segment(path.bounds.bottomRight), new Segment(path2.bounds.bottomLeft));
      path5.strokeColor = "black";
      path5.dashArray = [3, 5];
    }

    // Labels
    if (!tracedimensions.iscrown) {
      let text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Centreline Invert";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Centreline Invert";
      text = new PointText(path1.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path1.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    } else {
      let text = new PointText(path.bounds.topLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Left Spring Level";
      text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
      text.justification = "right";
      text.fillColor = "black";
      text.content = "Right Spring Level";
    }
    let text = new PointText(path.bounds.leftCenter.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Centreline Crown";
    drawTunnelHeader({ Path, Point, PointText, bounds: path.bounds, direction: tracedimensions.direction, inclination: tracedimensions.inclination, style });
  };

  /**
   * Calculate trace points (P2, P4, P5, XPmid) based on orientation
   * @param {Object} dimension - Trace dimension object
   * @param {number} D - Tunnel direction in degrees
   * @param {number} Dir - Dip direction in degrees
   * @param {number} Alpha - Alpha angle in degrees
   * @param {number} TanAlpha - Tan(Alpha)
   * @param {number} TanBeta - Tan(Beta)
   * @param {Object} quadrants - Quadrant boundaries
   * @param {number} ApDir - Apparent direction in degrees
   * @param {number} Dopp - Opposite direction in degrees
   * @returns {Object} Trace points: XP2, YP2, XP4, YP4, XP5, YP5, XPmid
   */
  const calculateTracePoints = (dimension, D, Dir, Alpha, TanAlpha, TanBeta, quadrants, ApDir, Dopp, sideApDir = ApDir) => {
    const orientation = dimension.orientation || props.orientation || "";
    let XP2, YP2, XP4, YP4, XP5, YP5, XPmid;

    const rd = (dimension.rd - startOffsetRD) * multiplier;
    XP2 = padding + rd;
    YP2 = Ymax - Arc;

    // The side the trace crosses to follows the strike (level-tunnel ApDir);
    // a folded inclined dip turns over only the lean (ApDir below).
    const Diff = D - Dir;
    if (sideApDir === D) {
      if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    } else {
      const DiffDopp = Dopp - Dir;
      if ((DiffDopp > -360 && DiffDopp < -270) || (DiffDopp > 0 && DiffDopp < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    }
    YP4 = Ymax - (3 * Arc);

    // Calculate XP5 and YP5
    if (XP2 > XP4) {
      XP5 = XPmid - r / TanAlpha;
    } else {
      XP5 = XPmid + r / TanAlpha;
    }
    YP5 = Ymax - (2 * Arc);

    // The base geometry above anchors the RIGHT SPL (XP2) at the input chainage.
    // For a Left-SPL or Centreline data point, shift the whole trace so the
    // CHOSEN surface crosses at the input chainage instead (matching how
    // dshape/horseshoe anchor the selected surface). The drawn crown-centreline
    // crossing is XPmid ∓ r/TanBeta (sign per ApDir).
    //
    // "Any Other Point" is a point on the face: side, offset d and height Hp
    // above the invert. Across the tunnel the plane moves along the log by
    // (XP4 - XP2) per diameter, and upwards by (clCross - XPmid) per radius, so
    // it reaches the point at x6. This holds for points inside the face as
    // well as on its surface.
    if (orientation === "leftspl" || orientation === "centerline" || orientation === "otherpoint") {
      const clCross = ApDir === D ? XPmid - r / TanBeta : XPmid + r / TanBeta;
      let anchor = orientation === "leftspl" ? XP4 : clCross;
      if (orientation === "otherpoint") {
        const d = dimension.tunnelside === "center" ? 0 : parseFloat(dimension.distancefromcenter) || 0;
        const Xp = (dimension.tunnelside === "left" ? -d : d) * multiplier;
        const Hp = (parseFloat(dimension.verticalheight) || 0) * multiplier;
        const perAcross = (XP4 - XP2) / (-2 * r);
        const perUp = (clCross - XPmid) / r;
        anchor = XP2 + perAcross * (Xp - r) + perUp * (Hp - r);
      }
      const shift = XP2 - anchor; // XP2 currently sits at the input chainage
      XP2 += shift;
      XP4 += shift;
      XPmid += shift;
      XP5 += shift;
    }

    return { XP2, YP2, XP4, YP4, XP5, YP5, XPmid };
  };

  /**
   * Plot right wall trace
   * @param {Object} params - Parameters object
   * @returns {Array} Trace union points
   */
  const plotRightWall = (params) => {
    const { XP2, XP4, XPmid, ApDir, D, TanBeta, usrvar, Ymax } = params;
    let traceunion = params.traceunion || [];
    let Xmin = params.Xmin;
    let Xmax = params.Xmax;
    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];

    for (let n = 90; n >= Theta; n--) {
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP5[n] = YP[n];
      }

      if (Xmin !== undefined) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax !== undefined) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }

    if (!tracedimensions.iscrown) {
      usrvar["P1"] = "(" + ((XP5[90] - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((Ymax - YP5[90]) / multiplier).toFixed(2) + ")";
    }

    return { traceunion, Xmin, Xmax, XP5, YP5 };
  };

  /**
   * Plot right crown trace
   * @param {Object} params - Parameters object
   * @returns {Object} Trace data
   */
  const plotRightCrown = (params) => {
    const { XP2, XP4, XPmid, ApDir, D, TanBeta, usrvar, calcYP2, calcYP5, Ymax } = params;
    let traceunion = params.traceunion || [];
    let Xmin = params.Xmin;
    let Xmax = params.Xmax;
    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];
    const scalearr = [];

    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      }
      usrvar["Prc" + n] =
        "XP2 = (" +
        (XP2 - padding) / multiplier +
        ") theta=(" +
        Theta +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      if (Xmin !== undefined) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax !== undefined) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }

    const rcrown = new Path({
      segments: scalearr,
    });

    // Y coordinates need to be inverted: (Ymax - YP) / multiplier
    // P2: YP2 = Ymax - Arc, so y = (Ymax - YP2) / multiplier = Arc / multiplier
    // P5: YP5 = Ymax - 2*Arc, so y = (Ymax - YP5) / multiplier = 2*Arc / multiplier
    usrvar["P2"] = "(" + ((XP2 - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((Ymax - calcYP2) / multiplier).toFixed(2) + ")";
    usrvar["P5"] = "(" + ((XP5[90] - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((Ymax - calcYP5) / multiplier).toFixed(2) + ")";

    return { traceunion, Xmin, Xmax, rcrown, crownXs: scalearr.map((p) => p[0]) };
  };

  /**
   * Plot left crown trace
   * @param {Object} params - Parameters object
   * @returns {Object} Trace data
   */
  const plotLeftCrown = (params) => {
    const { XP2, XP4, XPmid, ApDir, D, TanBeta, usrvar, calcYP4, Ymax } = params;
    let traceunion = params.traceunion || [];
    let Xmin = params.Xmin;
    let Xmax = params.Xmax;
    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];
    const scalearr = [];

    for (let n = 90; n >= Theta; n--) {
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      if (Xmin !== undefined) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax !== undefined) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
      usrvar["Plc" + n] =
        "XP4 = (" +
        (XP4 - padding) / multiplier +
        ") XPmid=(" +
        (XPmid - padding) / multiplier +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
    }

    const lcrown = new Path({
      segments: scalearr,
    });

    // P4: YP4 = Ymax - 3*Arc, so y = (Ymax - YP4) / multiplier = 3*Arc / multiplier
    usrvar["P4"] = "(" + ((XP4 - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((Ymax - calcYP4) / multiplier).toFixed(2) + ")";

    return { traceunion, Xmin, Xmax, lcrown, crownXs: scalearr.map((p) => p[0]) };
  };

  /**
   * Plot left wall trace
   * @param {Object} params - Parameters object
   * @returns {Object} Trace data
   */
  const plotLeftWall = (params) => {
    const { XP2, XP4, XPmid, ApDir, D, TanBeta, usrvar, Ymax } = params;
    let traceunion = params.traceunion || [];
    let Xmin = params.Xmin;
    let Xmax = params.Xmax;
    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];

    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      if (Xmin !== undefined) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax !== undefined) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
      usrvar["Plw" + n] =
        "XP4 = (" +
        (XP4 - padding) / multiplier +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
    }

    if (!tracedimensions.iscrown) {
      // P3 is at YP5[90] from left wall, which is at Ymax - 4*Arc
      // Y coordinate: (Ymax - YP5[90]) / multiplier = 4*Arc / multiplier
      usrvar["P3"] = "(" + ((XP5[90] - padding) / multiplier + startOffsetRD).toFixed(2) + "," + ((Ymax - YP5[90]) / multiplier).toFixed(2) + ")";
    }

    return { traceunion, Xmin, Xmax, XP5 };
  };

  /**
   * Draw P6 point on tunnel face/heading
   * @param {Object} dimension - Trace dimension object
   * @param {number} index - Trace index for color
   * @param {Object} style - Style object for the trace
   * @param {Array} discontinuityInputPoints - Array to store discontinuity input points
   * @param {Object} fixedTextPosition - Reference to store fixed text position
   */
  const drawP6Point = (dimension, index, style, discontinuityInputPoints, fixedTextPosition) => {
    drawFacePoint(dimension, index, style, discontinuityInputPoints, fixedTextPosition);
    // Face logs carry a known point on each face; both are data locations.
    const second = faceLogSecondPoint(dimension);
    if (second) drawFacePoint({ ...dimension, ...second }, index, style, discontinuityInputPoints, fixedTextPosition, 10);
  };

  const drawFacePoint = (dimension, index, style, discontinuityInputPoints, fixedTextPosition, labelLift = 0) => {
    const orientation = dimension.orientation || props.orientation || "";
    if (orientation !== "otherpoint") return;

    const P1y = tracedimensions.iscrown ? padding + Arc : padding;
    const P3y = tracedimensions.iscrown ? padding + 3 * Arc : padding + 4 * Arc;
    const rdxcoordinate = chainageToCanvasX(dimension.rd, padding, multiplier, startOffsetRD);

    const rdLine = new Path.Line(
      new Point(rdxcoordinate, P1y - 7),
      new Point(rdxcoordinate, P3y)
    );
    rdLine.style = { ...style, dashArray: [4, 4] };
    rdLine.strokeColor = colors[index];
    rdLine.strokeWidth = 1;

    // Two faces a few metres apart would print their labels over each other.
    const tunnelFaceText = new PointText(new Point(rdxcoordinate - 5, P1y - 10 - labelLift));
    tunnelFaceText.content = "Tunnel Face";
    tunnelFaceText.style = { fontSize: 8 };
    tunnelFaceText.fillColor = colors[index];
    tunnelFaceText.justification = "center";

    const arrowDown = new Path.Line(
      new Point(rdxcoordinate, P1y - 5),
      new Point(rdxcoordinate, P1y - 2)
    );
    arrowDown.style = style;
    arrowDown.strokeColor = colors[index];
    arrowDown.strokeWidth = 1;

    const arrowLeft = new Path.Line(
      new Point(rdxcoordinate, P1y),
      new Point(rdxcoordinate - 3, P1y - 3)
    );
    arrowLeft.style = style;
    arrowLeft.strokeColor = colors[index];
    arrowLeft.strokeWidth = 1;

    const arrowRight = new Path.Line(
      new Point(rdxcoordinate, P1y),
      new Point(rdxcoordinate + 3, P1y - 3)
    );
    arrowRight.style = style;
    arrowRight.strokeColor = colors[index];
    arrowRight.strokeWidth = 1;

    if (legendpath["path2"]) {
      let path2center = legendpath["path2"].bounds.center;
      // The face sketch lies on its side: up the face is towards the log,
      // and the Right Side is downwards.
      const Hp = parseFloat(dimension.verticalheight) || 0;
      const d = dimension.tunnelside === "center" ? 0 : parseFloat(dimension.distancefromcenter) || 0;
      const xOffset = -(Hp * multiplier - r);
      const yOffset = (dimension.tunnelside === "left" ? -d : d) * multiplier;

      path2center = new Point(path2center.x + xOffset, path2center.y + yOffset);

      const tunnelDotOnLegend = new Path.Circle(path2center, 2);
      tunnelDotOnLegend.style = { ...style, fillColor: colors[index] };

      discontinuityInputPoints.push({
        point: path2center,
        style: style,
        color: colors[index]
      });

      // A face point on the tunnel surface is also dotted on the log, like a
      // named one. Its bottom point is the seam of the unrolled circle and
      // appears at both edges.
      const R = r / multiplier;
      const X = dimension.tunnelside === "left" ? -d : d;
      if (Math.abs(Math.hypot(X, Hp - R) - R) <= 0.01) {
        let theta = Math.atan2(X, R - Hp);
        if (theta < -1e-9) theta += 2 * Math.PI;
        const ylogs = Math.abs(theta) < 1e-9 ? [0, 2 * Math.PI * R] : [theta * R];
        const minY = tracedimensions.iscrown ? Math.PI * R / 2 : 0;
        const maxY = tracedimensions.iscrown ? 3 * Math.PI * R / 2 : 2 * Math.PI * R;
        ylogs.filter((y) => y >= minY - 1e-9 && y <= maxY + 1e-9).forEach((y) => {
          const dot = new Path.Circle(new Point(rdxcoordinate, Ymax - y * multiplier), 2);
          dot.style = { ...style, fillColor: colors[index] };
        });
      }
    }
  };

  const createTrace = () => {
    const discontinuityInputPoints = [];
    let fixedTextPosition = null;
    if (legendpath["path2"]) {
      const path2Center = legendpath["path2"].bounds.center;
      fixedTextPosition = new Point(path2Center.x + 30, path2Center.y - 30);
    }

    const orientationLocationMap = {
      leftinvert: "Left Invert",
      leftspl: "Left SPL",
      rightinvert: "Right Invert",
      rightspl: "Right SPL",
      centerline: "Centreline",
      otherpoint: "",
    };
    const tunnelSideLabelMap = { left: "Left", right: "Right", center: "Centre" };
    const inputRows = [];
    const outputRows = [];
    // Distinct horizontal-trace alerts raised once each (the flat-plane alert is
    // surfaced at the input page; only the strike-parallel alert is raised here).
    const horizontalAlerts = createTraceAlerts();
    const clipAlerts = createTraceAlerts();

    const fmt1 = (v) => Number(v).toFixed(1).replace(/\.0$/, "");
    const fmtAsf = (v) => Number(v).toFixed(2).replace(/\.?0+$/, "");
    // A chainage is "-" where the trace has no single crossing of that line.
    const oneDecimal = (value) => {
      const n = Number(value);
      return value === "-" || value === undefined || !isFinite(n) ? "-" : n.toFixed(1);
    };

    // d = 0 is the centreline whichever side was named.
    const tunnelSideLabel = (dimension) =>
      dimension.tunnelside === "center" || parseFloat(dimension.distancefromcenter) === 0
        ? tunnelSideLabelMap.center
        : tunnelSideLabelMap[dimension.tunnelside];
    // A trace whose orientation came from known chainages has no face-heading
    // point to report; the three chainages the user entered are the useful
    // record, so they take those columns and the sub-group is relabelled.
    const isChainageSource = (dimension) =>
      (dimension.orientationsource || "known") === "chainages";
    const chainageCell = (v) =>
      v === undefined || v === null || v === "" ? "-" : String(v);

    // One Inputs-table row per trace, the same for every plotting branch.
    // Tunnel-surface rows show their location name; only Face (Heading) rows
    // ("Any Other Point") carry side / distance / height data.
    const appendInputRow = (dimension, index, DirInput) => {
      const traceOrientation = dimension.orientation || props.orientation || "";
      const isP6 = traceOrientation === "otherpoint";
      const p6Cell = (v) => (isP6 && v !== undefined && v !== "" ? String(v) : "-");
      const row = {
        no: String(index + 1),
        label: labelCell(dimension),
        dir: String(DirInput),
        amt: String(parseInt(dimension.amt)),
        ch: String(dimension.rd),
        surface: orientationLocationMap[traceOrientation] || "-",
        side: isP6 ? tunnelSideLabel(dimension) || "-" : "-",
        dist: p6Cell(dimension.tunnelside === "center" ? 0 : dimension.distancefromcenter),
        ht: p6Cell(dimension.verticalheight),
        color: colors[index],
        ...(isChainageSource(dimension)
          ? {
              source: "chainages",
              side: chainageCell(dimension.p4),
              dist: chainageCell(dimension.p2),
              ht: chainageCell(dimension.p5),
            }
          : { source: dimension.orientationsource || "known" }),
      };
      inputRows.push(row);
      // Face logs carry a known point on each face; both are inputs.
      if ((dimension.orientationsource || "known") !== "facelogs") return;
      Object.assign(row, faceLogPointRow(dimension, 1));
      // The second line belongs to the same discontinuity: no repeat of its
      // number, label or orientation.
      inputRows.push({
        no: " ",
        label: " ",
        dir: " ",
        amt: " ",
        ...faceLogPointRow(dimension, 2),
        color: colors[index],
        source: "facelogs",
      });
    };

    props.dimensions.forEach((dimension, index) => {
      const style = {
        fillColor: "transparent",
        strokeColor: colors[index],
        strokeWidth: 1,
      };
      let traceunion = [];
      // A 360° input means 0°.
      const Draw = parseInt(tracedimensions.direction);
      const D = Draw === 360 ? 0 : Draw;
      // The degenerate-case gates test their angle for exactly 0, which is only
      // ever true of whole degrees, so they read the rounded pair.
      const DirRounded = parseInt(dimension.dir);
      const amtRounded = parseInt(dimension.amt);
      // Preserve the user-entered dip direction for the Input table (360 -> 0).
      const DirInput = DirRounded === 360 ? 0 : DirRounded;

      // Everything else plots from the unrounded pair: a computed orientation
      // keeps its exact figure, since rounding a bearing walks the trace along
      // the log.
      const exactOr = (v, fallback) => {
        const n = Number(v);
        return v === "" || v === null || v === undefined || !isFinite(n) ? fallback : n;
      };
      const amt = exactOr(dimension.amtExact, Number(dimension.amt));
      let Dir = exactOr(dimension.dirExact, DirRounded);
      if (Dir === 360) Dir = 0;
      // I is signed: positive falls along D, negative rises.
      const I = parseFloat(tracedimensions.inclination) || 0;

      // Strike-parallel, level and axis-parallel planes have no slope for the
      // main routine to divide by; they are intersected with the circle exactly.
      const exact = solveCircularAlphaZero({
        D, Dir: DirInput, amt: amtRounded, DirExact: Dir, amtExact: amt, I,
        R: Number(tracedimensions.radiusofcrown), dimension,
      });
      if (exact) {
        appendInputRow(dimension, index, DirInput);
        const R = Number(tracedimensions.radiusofcrown);
        const circumference = 2 * Math.PI * R;
        const minY = tracedimensions.iscrown ? Math.PI * R / 2 : 0;
        const maxY = tracedimensions.iscrown ? 3 * Math.PI * R / 2 : circumference;
        const toPoint = (rd, y) => new Point(chainageToCanvasX(rd, padding, multiplier, startOffsetRD), Ymax - y * multiplier);
        const paths = [];
        let extent = NO_EXTENT;
        if (exact.parallel) {
          exact.traces.forEach((t) => {
            // The bottom point is the seam: the same trace appears at both
            // edges of a full unrolled circle, not inside a flat invert.
            const levels = Math.abs(t.Ylog) < 1e-9 ? [0, circumference] : [t.Ylog];
            levels.filter((y) => y >= minY - 1e-9 && y <= maxY + 1e-9).forEach((y) => {
              const line = new Path.Line(toPoint(tracedimensions.startrd, y), toPoint(tracedimensions.endrd, y));
              line.style = style;
              paths.push(line);
            });
          });
          if (paths.length) extent = fullLogExtent(tracedimensions.startrd, tracedimensions.endrd);
        } else {
          const curve = new Path(exact.points.map((p) => toPoint(p.RD, p.Ylog)));
          curve.style = style;
          paths.push(curve);
          const shown = exact.points.filter((p) => p.Ylog >= minY - 1e-9 && p.Ylog <= maxY + 1e-9);
          const rds = shown.map((p) => p.RD);
          if (rds.length) {
            extent = chainageExtent(Math.min(...rds), Math.max(...rds));
          }
          if (rds.length && runsPastLog(Math.min(...rds), Math.max(...rds), tracedimensions.startrd, tracedimensions.endrd)) {
            clipAlerts.add(CLIP_ALERT, index);
          }
        }
        horizontalAlerts.add(exact.alert, index);
        const clip = new Path.Rectangle(new Rectangle(
          toPoint(tracedimensions.startrd, maxY), new Size(length, (maxY - minY) * multiplier)));
        const group = new Group([clip, ...paths]);
        group.clipped = true;
        const orientation = dimension.orientation;
        const level = { rightspl: Math.PI * R / 2, centerline: Math.PI * R, leftspl: 3 * Math.PI * R / 2 }[orientation];
        if ((dimension.orientationsource || "known") === "chainages") {
          [[dimension.p4, 3 * Math.PI * R / 2], [dimension.p2, Math.PI * R / 2]].forEach(([ch, y]) => {
            const dot = new Path.Circle(toPoint(Number(ch), y), 2);
            dot.style = { ...style, fillColor: colors[index] };
          });
        }
        if (level !== undefined) {
          const dot = new Path.Circle(toPoint(dimension.rd, level), 2);
          dot.style = { ...style, fillColor: colors[index] };
        } else if (orientation === "otherpoint") {
          drawP6Point(dimension, index, style, discontinuityInputPoints, fixedTextPosition);
        }
        const crossings = exact.crossings;
        // A crown-only log has no invert to cross.
        const invertCh = crossings && !tracedimensions.iscrown ? crossings.invert.toFixed(1) : "-";
        outputRows.push({
          no: String(index + 1), angle: fmt1(exact.alpha), adip: fmt1(exact.adip),
          adipi: fmt1(exact.adipi),
          chci: invertCh,
          chlspl: crossings ? crossings.leftSPL.toFixed(1) : "-",
          chrspl: crossings ? crossings.rightSPL.toFixed(1) : "-",
          chcl: crossings ? crossings.crown.toFixed(1) : "-",
          tsp: exact.parallel ? "∞" : fmt1(tracedimensions.iscrown ? exact.crownSpan : exact.span),
          ...extent,
          asf: fmtAsf(exact.asf),
          color: colors[index],
        });
        updateUserVariable({ Alpha: exact.alpha, BetaHorizontal: exact.adip, Beta: exact.adipi, Inclination: I,
          Tracelength: exact.parallel ? "∞" : exact.length, Asf: exact.asf });
        return;
      }

      const Alpha = calculateAlpha(D, Dir);
      const TanAlpha = Math.tan(toRadians(Alpha));
      let TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));
      const quadrants = calculateQuadrants(D);
      let ApDir = calculateApDir(D, Dir, quadrants);
      const Dopp = calculateDopp(D);
      // True apparent dip direction (horizontal reference) — the inclination
      // correction below may flip ApDir, but Asf needs the un-flipped one.
      const ApDirTrue = ApDir;

      // Apply Tunnel Inclination 'I' adjustment to Beta and ApDir
      const BetaHorizontal = toDegree(Math.atan(TanBeta));
      let Beta = BetaHorizontal;
      if (I !== 0) {
        const corrected = correctApparentDip(Beta, ApDir, D, I);
        Beta = corrected.beta;
        ApDir = corrected.apDir;
        TanBeta = Math.tan(toRadians(Beta));
      }

      // Asf — Apparent Spacing Factor (Algorithm_Additional Xlog3D_Asf_10Aug26)
      const Asf = computeAsf({ amt, alpha: Alpha, inclination: I, apDirTrue: ApDirTrue, dopp: Dopp });

      const tracePoints = calculateTracePoints(dimension, D, Dir, Alpha, TanAlpha, TanBeta, quadrants, ApDir, Dopp, ApDirTrue);
      const { XP2, XP4, XP5, XPmid, YP2: calcYP2, YP4: calcYP4, YP5: calcYP5 } = tracePoints;

      let Xmin, Xmax;
      const usrvar = {};
      usrvar["Asf"] = Asf;
      usrvar["Inclination"] = I;
      usrvar["BetaHorizontal"] = BetaHorizontal;

      if (!tracedimensions.iscrown) {
        const rightWallResult = plotRightWall({
          XP2,
          XP4,
          XPmid,
          ApDir,
          D,
          TanBeta,
          traceunion,
          Xmin,
          Xmax,
          usrvar,
          Ymax,
        });
        traceunion = rightWallResult.traceunion;
        Xmin = rightWallResult.Xmin;
        Xmax = rightWallResult.Xmax;
      }

      const rightCrownResult = plotRightCrown({
        XP2,
        XP4,
        XPmid,
        ApDir,
        D,
        TanBeta,
        traceunion,
        Xmin,
        Xmax,
        usrvar,
        calcYP2,
        calcYP5,
        Ymax,
      });
      traceunion = rightCrownResult.traceunion;
      Xmin = rightCrownResult.Xmin;
      Xmax = rightCrownResult.Xmax;
      const rcrown = rightCrownResult.rcrown;

      const leftCrownResult = plotLeftCrown({
        XP2,
        XP4,
        XPmid,
        ApDir,
        D,
        TanBeta,
        traceunion,
        Xmin,
        Xmax,
        usrvar,
        calcYP4,
        Ymax,
      });
      traceunion = leftCrownResult.traceunion;
      Xmin = leftCrownResult.Xmin;
      Xmax = leftCrownResult.Xmax;
      const lcrown = leftCrownResult.lcrown;

      if (!tracedimensions.iscrown) {
        const leftWallResult = plotLeftWall({
          XP2,
          XP4,
          XPmid,
          ApDir,
          D,
          TanBeta,
          traceunion,
          Xmin,
          Xmax,
          usrvar,
          Ymax,
        });
        traceunion = leftWallResult.traceunion;
        Xmin = leftWallResult.Xmin;
        Xmax = leftWallResult.Xmax;
      }

      const union = new Path({
        segments: traceunion,
        style: style,
      });
      if (!tracedimensions.iscrown) {
        const tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] = (rcrown["length"] + lcrown["length"]) / multiplier;
      } else {
        const tracelength = union["length"] / multiplier;
        usrvar["Tracelength"] = tracelength;
        usrvar["Tracecrown"] = tracelength;
      }

      // Clip visible trace to the log rectangle so near-horizontal traces don't extend past it
      const unionBounds = union.bounds;
      const traceExtendsBeyondLog = drawnPastLog(unionBounds.left, unionBounds.right, {
        padding, multiplier, startOffsetRD, length,
      });
      const traceClip = new Path.Rectangle(
        new Rectangle(new Point(padding, padding), new Size(length, 2 * (Arc + Arc)))
      );
      const traceGroup = new Group([traceClip, union]);
      traceGroup.clipped = true;

      const orientation = dimension.orientation || props.orientation || "";
      if (orientation === "otherpoint") {
        if (!legendpath["path2"]) {
          console.error("Tunnel face (path2) not found in legendpath");
          return;
        }

        drawP6Point(dimension, index, style, discontinuityInputPoints, fixedTextPosition);
      }

      // Data-point-location dot on the log for the fixed surface options — the
      // trace's crossing of the chosen surface (Left SPL / Right SPL / Crown
      // centreline), matching the dot dshape/horseshoe draw for these options.
      // Data-point-location dot on the log: the trace now crosses the chosen
      // surface exactly at the input chainage, so mark that point (input chainage
      // × selected surface level), matching the dot dshape/horseshoe draw.
      if (orientation === "leftspl" || orientation === "rightspl" || orientation === "centerline") {
        const rdX = padding + (dimension.rd - startOffsetRD) * multiplier;
        const surfaceY =
          orientation === "leftspl" ? calcYP4 : orientation === "rightspl" ? calcYP2 : calcYP5;
        const dpDot = new Path.Circle(new Point(rdX, surfaceY), 2);
        dpDot.style = { ...style, fillColor: colors[index] };
      }
      // Known chainages anchor at the centreline; the SPL chainages entered
      // alongside it are data points too.
      if ((dimension.orientationsource || "known") === "chainages") {
        [[dimension.p4, calcYP4], [dimension.p2, calcYP2]].forEach(([ch, y]) => {
          const dot = new Path.Circle(new Point(padding + (Number(ch) - startOffsetRD) * multiplier, y), 2);
          dot.style = { ...style, fillColor: colors[index] };
        });
      }

      if (traceExtendsBeyondLog) {
        clipAlerts.add(CLIP_ALERT, index);
      }

      // Beta was inclination-adjusted earlier in this scope.
      const Span = (Math.abs(Xmax - Xmin) / multiplier).toFixed(1).replace(/\.0$/, "");
      // A crown-only log shows only the part of the trace above the SPLs,
      // closed at the trace's SPL crossings.
      const crownXs = [...rightCrownResult.crownXs, ...leftCrownResult.crownXs, XP2, XP4];
      const crownMin = Math.min(...crownXs);
      const crownMax = Math.max(...crownXs);
      const [xLo, xHi] = tracedimensions.iscrown ? [crownMin, crownMax] : [Xmin, Xmax];
      const extent = chainageExtent(
        (xLo - padding) / multiplier + startOffsetRD,
        (xHi - padding) / multiplier + startOffsetRD
      );

      // Extract X-only chainages for Ch. output
      // Circular mapping: P1=LeftInvert, P2=RightSPL, P3=RightInvert, P4=LeftSPL, P5=CentreLine
      const extractX = (pStr) => pStr ? pStr.slice(1, pStr.indexOf(",")) : "-";
      usrvar["P1x"] = extractX(usrvar["P1"]);
      usrvar["P2x"] = extractX(usrvar["P2"]);
      usrvar["P3x"] = extractX(usrvar["P3"]);
      usrvar["P4x"] = extractX(usrvar["P4"]);
      usrvar["P5x"] = extractX(usrvar["P5"]);

      // Collect per-trace row data for the Inputs / Outputs tables.
      // Tunnel-surface rows show their location name; only Face (Heading)
      // rows ("Any Other Point") carry side / distance / LocSPL / height data.
      appendInputRow(dimension, index, DirInput);
      outputRows.push({
        no: String(index + 1),
        angle: fmt1(Alpha),
        adip: fmt1(BetaHorizontal),
        adipi: fmt1(Beta),
        chci: oneDecimal(usrvar["P1x"]),
        chlspl: oneDecimal(usrvar["P4x"]),
        chrspl: oneDecimal(usrvar["P2x"]),
        chcl: oneDecimal(usrvar["P5x"]),
        tsp: String(Span),
        ...extent,
        asf: fmtAsf(Asf),
        color: colors[index],
      });

      updateUserVariable(usrvar);
    });
    
    // Raise each distinct alert once, naming the traces that raised it. Only
    // new or edited traces pop up; the rest are already on the printed list.
    // A redraw of the same traces keeps the pop-ups already showing; any
    // change drops them (and any left over from the input page) first.
    const popUp = changedTraces("circular", props.dimensions);
    if (popUp.size) removeAllToasts();
    const clipPopUps = clipAlerts.messages(popUp);
    if (clipPopUps.length && window.audio) window.audio.play();
    clipPopUps.forEach((message) => {
      addToast(message, { appearance: "info" });
    });
    horizontalAlerts.messages(popUp).forEach((message) => {
      addToast(message, { appearance: "warning" });
    });

    if (fixedTextPosition && discontinuityInputPoints.length > 0) {
      const linepath2centerText = new PointText(fixedTextPosition);
      linepath2centerText.content = "Discontinuity data location";
      linepath2centerText.style = { fontSize: 8 };
      linepath2centerText.fillColor = "black";

      discontinuityInputPoints.forEach(({ point, style, color }) => {
        const linepath2center = new Path.Line(point, fixedTextPosition);
        linepath2center.style = { ...style, dashArray: [3, 3] };
        linepath2center.strokeColor = color;
      });
    }

    // ---- Tabulated Inputs / Outputs (per 17May26 Trace Page spec) ----
    const drawBorder = (x1, y1, x2, y2) => {
      const p = new Path.Line(new Point(x1, y1), new Point(x2, y2));
      p.strokeColor = "black";
      p.strokeWidth = 0.5;
      return p;
    };
    const cellText = (text, xL, xR, yT, yB, opts = {}) => {
      const t = new PointText(new Point((xL + xR) / 2, (yT + yB) / 2 + 3));
      t.justification = "center";
      t.fillColor = opts.color || "black";
      if (opts.bold) t.fontWeight = "bold";
      t.fontSize = opts.fontSize || 10;
      t.content = text;
      return t;
    };
    const buildCols = (xStart, defs) => {
      let x = xStart;
      return defs.map((d) => {
        const col = { ...d, left: x, right: x + d.width };
        x += d.width;
        return col;
      });
    };
    const noteLine = (yPos, text, opts = {}) => {
      const n = new PointText(new Point(padding + (opts.indent || 0), yPos));
      n.justification = "left";
      n.fillColor = "black";
      if (opts.bold) n.fontWeight = "bold";
      n.fontSize = opts.fontSize || 9;
      n.content = text;
      return n;
    };

    let curY = points + padding + 2 * (hw + Arc);

    // ----- Inputs section -----
    const inLabel = new PointText(new Point(padding, curY));
    inLabel.justification = "left";
    inLabel.fillColor = "black";
    inLabel.fontWeight = "bold";
    inLabel.content = "Inputs:";
    curY += 18;
    const geomText = new PointText(new Point(padding, curY));
    geomText.justification = "left";
    geomText.fillColor = "black";
    const I0 = parseFloat(tracedimensions.inclination) || 0;
    const D0 = parseInt(tracedimensions.direction);
    geomText.content =
      "Tunnel Direction: " + D0 +
      "; Tunnel Inclination: " + I0 +
      "; Radius: " + (r / multiplier).toFixed(1).replace(/\.0$/, "");
    curY += 20;

    // Input table
    const inColDefs = [
      { key: "no", label: "D.No.", width: 45 },
      { key: "label", label: "Label", width: 45 },
      { key: "dir", label: "DipDir", width: 50 },
      { key: "amt", label: "DipAmt", width: 50 },
      { key: "ch", label: "Chainage", width: 60 },
      { key: "surface", label: "Tunnel Surface", width: 90 },
      { key: "side", label: "Side", width: 45 },
      { key: "dist", label: "Distance", width: 60 },
      { key: "ht", label: "Height", width: 50 },
    ];
    // With every trace sourced from known chainages there is no face heading to
    // report, so the last three columns carry the chainages that were entered.
    const allFromChainages =
      inputRows.length > 0 && inputRows.every((r) => r.source === "chainages");
    const colIdx = (key) => inColDefs.findIndex((c) => c.key === key);
    if (allFromChainages) {
      Object.assign(inColDefs[colIdx("side")], { label: "Ch. Left SPL", width: 65 });
      Object.assign(inColDefs[colIdx("dist")], { label: "Ch. Right SPL", width: 70 });
      Object.assign(inColDefs[colIdx("ht")], { label: "Ch. Centreline", width: 75 });
    }
    const inCols = buildCols(padding, inColDefs);
    const inGroupStartIdx = colIdx("ch"); // "Discontinuity Data Location" group
    const inFaceStartIdx = colIdx("side"); // "Face (Heading)" sub-group
    const inLeftX = inCols[0].left;
    const inRightX = inCols[inCols.length - 1].right;
    const inHeaderRowH = 18;
    const inDataRowH = 18;
    const inHeaderY1 = curY;
    const inHeaderY2 = inHeaderY1 + inHeaderRowH;
    const inHeaderY3 = inHeaderY2 + inHeaderRowH;
    const inDataY0 = inHeaderY3 + inHeaderRowH;
    const inTableBottomY = inDataY0 + inDataRowH * inputRows.length;

    inCols.forEach((col, i) => {
      if (i < inGroupStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY1, inDataY0, { bold: true });
      }
    });
    cellText(
      "Discontinuity Data Location",
      inCols[inGroupStartIdx].left,
      inRightX,
      inHeaderY1,
      inHeaderY2,
      { bold: true }
    );
    inCols.forEach((col, i) => {
      if (i >= inGroupStartIdx && i < inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY2, inDataY0, { bold: true });
      }
    });
    cellText(
      allFromChainages ? "Known Trace Chainages (m)" : "Face (Heading)",
      inCols[inFaceStartIdx].left,
      inRightX,
      inHeaderY2,
      inHeaderY3,
      { bold: true }
    );
    inCols.forEach((col, i) => {
      if (i >= inFaceStartIdx) {
        cellText(col.label, col.left, col.right, inHeaderY3, inDataY0, { bold: true });
      }
    });
    inputRows.forEach((row, r) => {
      const yT = inDataY0 + r * inDataRowH;
      const yB = yT + inDataRowH;
      inCols.forEach((col) => {
        cellText(row[col.key] || "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(inLeftX, inHeaderY1, inRightX, inHeaderY1);
    drawBorder(inLeftX, inTableBottomY, inRightX, inTableBottomY);
    drawBorder(inLeftX, inHeaderY1, inLeftX, inTableBottomY);
    drawBorder(inRightX, inHeaderY1, inRightX, inTableBottomY);
    inCols.forEach((col, i) => {
      if (i === 0) return;
      let startY = inHeaderY1;
      if (i > inFaceStartIdx) startY = inHeaderY3;
      else if (i > inGroupStartIdx) startY = inHeaderY2;
      drawBorder(col.left, startY, col.left, inTableBottomY);
    });
    drawBorder(inCols[inGroupStartIdx].left, inHeaderY2, inRightX, inHeaderY2);
    drawBorder(inCols[inFaceStartIdx].left, inHeaderY3, inRightX, inHeaderY3);
    drawBorder(inLeftX, inDataY0, inRightX, inDataY0);
    for (let r = 1; r < inputRows.length; r++) {
      const y = inDataY0 + r * inDataRowH;
      drawBorder(inLeftX, y, inRightX, y);
    }
    curY = inTableBottomY + 14;

    const inputNotesLabel = noteLine(curY, "Notes:", { bold: true });
    noteLine(curY, " D.No. (Discontinuity number); Label (Discontinuity label); DipDir (Dip direction); DipAmt (Dip amount); Chainage (RD); Tunnel Surface", {
      indent: inputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    const inputNotesEnd = noteLine(
      curY,
      allFromChainages
        ? "(data point location on tunnel surface); Known Trace Chainages: the chainages entered for the trace at left SPL, right SPL and the centreline."
        : "(data point location on tunnel surface); Face (Heading): Side (wrt tunnel centreline), Distance (from centreline), Height (from invert)."
    );
    // Key to the dot that marks each data point on the log.
    const dataPointKeyX = inputNotesEnd.bounds.right + 10;
    const dataPointKeyDot = new Path.Circle(new Point(dataPointKeyX, curY - 3), 2);
    dataPointKeyDot.style = { fillColor: "black", strokeColor: "black", strokeWidth: 1 };
    noteLine(curY, "Data Point", { indent: dataPointKeyX + 5 - padding });
    curY += 22;

    // ----- Outputs section -----
    const outLabel = new PointText(new Point(padding, curY));
    outLabel.justification = "left";
    outLabel.fillColor = "black";
    outLabel.fontWeight = "bold";
    outLabel.content = "Outputs:";
    curY += 18;
    const crownText = new PointText(new Point(padding, curY));
    crownText.justification = "left";
    crownText.fillColor = "black";
    crownText.content =
      "Crown circumference: " + (ArcFull / multiplier).toFixed(1).replace(/\.0$/, "");
    curY += 20;

    const outColDefs = [
      { key: "no", label: "D.No.", width: 42 },
      { key: "angle", label: "Angle", width: 45 },
      { key: "adip", label: "ADip", width: 45 },
      { key: "adipi", label: "ADipI", width: 50 },
      { key: "chci", label: "ChCI", width: 45 },
      { key: "chlspl", label: "ChLSPL", width: 55 },
      { key: "chrspl", label: "ChRSPL", width: 55 },
      { key: "chcl", label: "ChCL", width: 50 },
      { key: "chmin", label: "ChMin", width: 50 },
      { key: "chmax", label: "ChMax", width: 50 },
      { key: "tsp", label: "TSp", width: 45 },
      { key: "asf", label: "Asf", width: 45 },
    ];
    const outCols = buildCols(padding, outColDefs);
    const outLeftX = outCols[0].left;
    const outRightX = outCols[outCols.length - 1].right;
    const outHeaderRowH = 18;
    const outDataRowH = 18;
    const outHeaderY = curY;
    const outDataY0 = outHeaderY + outHeaderRowH;
    const outTableBottomY = outDataY0 + outDataRowH * outputRows.length;

    outCols.forEach((col) => {
      cellText(col.label, col.left, col.right, outHeaderY, outDataY0, { bold: true });
    });
    outputRows.forEach((row, r) => {
      const yT = outDataY0 + r * outDataRowH;
      const yB = yT + outDataRowH;
      outCols.forEach((col) => {
        const v = row[col.key];
        cellText(v != null ? String(v) : "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(outLeftX, outHeaderY, outRightX, outHeaderY);
    drawBorder(outLeftX, outTableBottomY, outRightX, outTableBottomY);
    drawBorder(outLeftX, outHeaderY, outLeftX, outTableBottomY);
    drawBorder(outRightX, outHeaderY, outRightX, outTableBottomY);
    outCols.forEach((col, i) => {
      if (i === 0) return;
      drawBorder(col.left, outHeaderY, col.left, outTableBottomY);
    });
    drawBorder(outLeftX, outDataY0, outRightX, outDataY0);
    for (let r = 1; r < outputRows.length; r++) {
      const y = outDataY0 + r * outDataRowH;
      drawBorder(outLeftX, y, outRightX, y);
    }
    curY = outTableBottomY + 14;

    // The persistence caution reads first, directly under the Outputs table.
    const cautionLabel = noteLine(curY, "Caution:", { bold: true });
    noteLine(curY, " Discontinuity trace span may be evaluated with care in consideration of its persistence.", {
      indent: cautionLabel.bounds.width + 2,
    });
    curY += 16;

    const outputAlerts = [...clipAlerts.messages(), ...horizontalAlerts.messages()];
    if (outputAlerts.length) {
      noteLine(curY, "Alerts:", { bold: true });
      curY += 14;
      outputAlerts.forEach((message) => {
        let line = noteLine(curY, "");
        let words = "";
        message.split(/\s+/).forEach((word) => {
          const candidate = words ? words + " " + word : word;
          line.content = candidate;
          if (line.bounds.width > outRightX - padding && words) {
            line.content = words;
            curY += 12;
            line = noteLine(curY, word);
            words = word;
          } else {
            words = candidate;
          }
        });
        curY += 16;
      });
    }

    // Output abbreviations. Labelled "Abbreviations used" rather than "Notes"
    // to separate it from the notes proper below — the Asf specification calls
    // these two things by those names.
    const outputNotesLabel = noteLine(curY, "Abbreviations used:", { bold: true });
    noteLine(curY, " Angle (Angle between strike and tunnel direction); ADip (Apparent Dip with horizontal);", {
      indent: outputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    noteLine(curY, "ADipI (Apparent Dip with tunnel axis); Trace chainage: ChCI (centreline invert), ChLSPL (left SPL),");
    curY += 12;
    noteLine(curY, "ChRSPL (right SPL), ChCL (centreline), ChMin (minimum of trace), ChMax (maximum of trace); TSp: Trace span along tunnel;");
    curY += 12;
    noteLine(curY, "Asf: Apparent Spacing Factor.");
    curY += 16;

    // Asf usage notes (Algorithm_Additional Xlog3D_Asf_10Aug26)
    noteLine(curY, "•  Divide True Spacing by the Apparent Spacing Factor Asf to get Apparent Spacing along the tunnel.");
    curY += 12;
    noteLine(curY, "•  True Spacing is the distance between two adjoining discontinuities of the same set across their dip planes. This is");
    curY += 12;
    noteLine(curY, "the shortest distance between the two.", { indent: 11 });
    curY += 12;
    noteLine(curY, "•  Apply Asf for computation of Apparent Thickness and Apparent Aperture along the tunnel.");
    curY += 12;
    // The last note carries a link to the TrueSpace tool. The notes are drawn
    // on the canvas, so this is a canvas item rather than an anchor: blue
    // underlined text, with a rectangle over it as the click target.
    const trueSpaceNote = noteLine(
      curY,
      "•  If True Spacing not available, get True Spacing from this app: "
    );
    const trueSpaceLink = noteLine(curY, TRUESPACE_LABEL, {
      indent: trueSpaceNote.bounds.width + 3,
    });
    trueSpaceLink.fillColor = "#0b57d0";
    const linkUnderline = new Path.Line(
      trueSpaceLink.bounds.bottomLeft.add([0, 1]),
      trueSpaceLink.bounds.bottomRight.add([0, 1])
    );
    linkUnderline.strokeColor = "#0b57d0";
    linkUnderline.strokeWidth = 0.6;
    // Handled by the canvas element rather than a paper item — see
    // projectPointFromEvent for why paper's own hit-testing is off by the
    // device pixel ratio here.
    const box = trueSpaceLink.bounds.expand(6);
    trueSpaceLinkBounds.current = {
      left: box.left,
      right: box.right,
      top: box.top,
      bottom: box.bottom,
    };
    curY += 16;

    if (paper.view.viewSize.height < curY + 30) {
      paper.view.viewSize = new Size(paper.view.viewSize.width, curY + 30);
    }
  };

  const draw = () => {
    createScale();
    createLog();
    createTrace();
  };

  const creategraph = () => {
    paper.project.clear();
    const width = Math.max(length + 450, padding + 700);
    const height = Math.max(
      4 * Arc + padding + 400 + (props.dimensions.length - 1) * 75,
      padding + 2 * (hw + Arc) + 350 + props.dimensions.length * 40
    );
    paper.view.viewSize = new Size(width, height);
    draw();
    paper.view.draw();
  };


  const addTrace = () => {
    props.addtrace();
  };

  const showDeleteTrace = () => {
    setShow(true);
  };

  const deleteTrace = () => {
    props.deletetrace(selectedTrace);
    setShow(false);
  };

  const selectTrace = (e) => {
    const value = e.target.value;
    setSelectedTrace(value);
  };

  return (
    <div id="page">
      <Header onBack={props.showinput} />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="row">
          <div className="col-md-12">
            <button
              onClick={() => {
                props.showinput();
              }}
              className="btn bg-green-dark mw-2 me-3"
            >
              Edit Input
            </button>
            <button onClick={addTrace} className="btn bg-green-dark me-3">
              Add Trace
            </button>
            {props.tracecount > 0 ? (
              <button onClick={showDeleteTrace} className="btn bg-danger me-3">
                Delete Trace
              </button>
            ) : (
              ""
            )}
            <DownloadMenu filename="circular-plot" screenLabel="Circular Tunnel — Plot" feedbackInputs={props.dimensions} />
          </div>
          <div className="col-md-6"></div>
          <div className="col-md-12 overflow-auto">
            {!props.showdebug ? <canvas id="paper-canvas" resize="true" onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} /> : <Debug uservariables={uservariables} />}
          </div>
        </div>
      </div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Select trace to delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {props.dimensions.map((dimension, index) => (
            <Form.Check
              onChange={selectTrace}
              inline
              value={index}
              style={{ color: colors[index] }}
              label={"Trace " + (index + 1)}
              name="group1"
              type="radio"
              id={index}
              key={index}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" onClick={deleteTrace}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Output;
